import {weakMemo} from '@packages/utilities/memo';
import {dissoc} from '@packages/utilities/key-map';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {
  VariantData,
  VariantDataMap,
  VariantStateKey,
  createClassName,
  emptyVariantDataMap,
  getUserlandPrefix,
  parseVariantStateKey,
  stringifyVariantStateKey,
} from '@packages/systems/style-legacy/selectors';
import {
  TEmptyRecord,
  TRecord,
  TTypeApplication,
  TTypeConstructor,
  ExpressionElement,
  ELiteral,
} from '@packages/systems/wfdl-ast';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  Types,
  Component,
  Expressions,
  ExpressionPath,
  matchExpression,
  Plugins,
  Type,
  AttributesMap,
  getSystem,
  withSystem,
} from '@packages/systems/wfdl';
import {isUserComponentInst} from '@packages/utilities/platform';
import {Map as IMap} from 'immutable';
import {getGuidNullable} from '@packages/systems/core/utils/NativeIdUtils';
import {getCurrentOrDefaultExpression} from '@packages/systems/core/component-conversions';
import {ActiveVariants} from '@packages/systems/style/types';
import {designerTranslation} from '@packages/systems/designer-translations';
import {confirm} from '@packages/systems/dialog';
import * as React from 'react';
import {View} from '@packages/systems/spring';
import {pluralize} from '@packages/utilities/string-utils';
import {BASE_VARIANT_DEFAULTS, DEFAULT_STYLE_VARIANT_LABEL} from './constants';

// IDs can start with numbers which would make them invalid class names.
// To get around this we use an internal prefix for internal style variant class names.
const classPrefix = 'w-variant-';
const terminalPseudoElementSelector = /::[a-z-]+(?:\([^)]*\))?$/;

/**
 * Helper function for creating new default Base component style variants.
 */
const createBaseStyleVariant = (): Component.StyleVariant => ({
  id: BASE_VARIANT_DEFAULTS.id,
  displayName: BASE_VARIANT_DEFAULTS.displayName,
});

export const defaultVariantOptions = [createBaseStyleVariant()];

export class ComponentStyleVariantData implements VariantData {
  optionIdCssName: string;

  constructor(optionIdCssName: string) {
    this.optionIdCssName = optionIdCssName;
  }

  getClassName() {
    return `${classPrefix}${this.optionIdCssName}`;
  }

  getAttributes() {
    return null;
  }

  getSelector(baseSelector: string) {
    const selector = `:where(.${this.getClassName()})`;
    const pseudoElementMatch = baseSelector.match(
      terminalPseudoElementSelector
    );

    if (pseudoElementMatch?.index != null) {
      const selectorPrefix = baseSelector.slice(0, pseudoElementMatch.index);
      const selectorSuffix = baseSelector.slice(pseudoElementMatch.index);
      return `${selectorPrefix}${selector}${selectorSuffix}`;
    }

    return `${baseSelector}${selector}`;
  }
}

const collectVariants = (
  mut_map: VariantDataMap,
  variants: Component.Variants
) => {
  for (const [fieldId, variantOptions] of Object.entries(variants)) {
    for (const variantOption of variantOptions) {
      mut_map.set(
        stringifyVariantStateKey(fieldId, variantOption.id),
        new ComponentStyleVariantData(variantOption.id)
      );
    }
  }
};

export const generateVariantDataMap = weakMemo(
  (components: Component.ComponentMap): VariantDataMap => {
    const result = new VariantDataMap();
    components.forEach((component) => {
      const {variants} = component;
      if (variants) collectVariants(result, variants);
    });
    return result.size === 0 ? emptyVariantDataMap : result;
  }
);

export const generateVariantDataMapFromSymbols = (
  symbols: Array<JSONNode>
): VariantDataMap => {
  const result = new VariantDataMap();
  for (const symbol of symbols) {
    const variants = symbol?.data?.sym?.variants;
    if (variants) collectVariants(result, variants);
  }
  return result.size === 0 ? emptyVariantDataMap : result;
};

export const createStyleVariantType = (
  pluginName: string,
  componentName: string,
  variantFieldId: string
) => {
  return TTypeApplication({
    con: TTypeApplication({
      con: TTypeApplication({
        con: TTypeConstructor(qualify(Plugins.name('Basic'), 'StyleVariant')),
        arg: TRecord({[variantFieldId]: TEmptyRecord}),
      }),
      arg: TRecord({[componentName]: TEmptyRecord}),
    }),
    arg: TRecord({[pluginName]: TEmptyRecord}),
  });
};

export const getStyleVariantTypeArguments = (styleVariantType: Type) => {
  const deconstructedType = Types.deconstructTypeApplication(styleVariantType);
  const args = deconstructedType ? deconstructedType.args : null;
  if (!args) {
    return {
      variantFieldId: undefined,
      componentName: undefined,
      pluginName: undefined,
    };
  }
  const variantFieldId = getArgument(args[0]);
  const componentName = getArgument(args[1]);
  const pluginName = getArgument(args[2]);
  return {variantFieldId, componentName, pluginName};
};

const getArgument = (arg: Type | undefined) => {
  const record = arg ? Types.getRecordFields(arg) : null;
  return record ? Object.keys(record)[0] : undefined;
};

/**
 * Returns the original `dataType` and `defaultData` reference when no repair is needed.
 * Only restores the first key in `component.variants` — the Designer
 * doesn't support multi-key components. Customizations on the deleted
 * field (label, defaultValue) are not recoverable.
 */
export const restoreOrphanedStyleVariantProps = (
  component: Component.ComponentType,
  componentName: string
): Component.ComponentType => {
  const variants = Component.getVariants(component);
  if (!variants) return component;
  const variantFieldId = Object.keys(variants)[0];
  if (!variantFieldId) return component;

  const dataType = Component.getDataType(component);
  const recordFields = Types.getRecordFields(dataType);
  if (!recordFields) return component;

  let restoredType = dataType;
  if (!(variantFieldId in recordFields)) {
    restoredType = TRecord({
      ...recordFields,
      [variantFieldId]: withSystem(
        {
          label: DEFAULT_STYLE_VARIANT_LABEL,
          defaultValue: 'base',
          isBindable: true,
          isLocalizable: true,
        },
        createStyleVariantType('__SitePlugin', componentName, variantFieldId)
      ),
    });
  }

  const defaultData = Component.getDefaultData(component);
  let restoredDefaultData = defaultData;
  if (restoredDefaultData) {
    const defaultDataPath: ExpressionPath = [
      {in: 'Function'},
      {in: 'Record', at: variantFieldId},
    ];
    const existing = Expressions.getIn(restoredDefaultData, defaultDataPath);
    if (!existing) {
      restoredDefaultData = Expressions.sparseSetIn(
        restoredDefaultData,
        defaultDataPath,
        ELiteral({
          name: qualify(Plugins.name('Basic'), typeName('StyleVariant')),
          value: BASE_VARIANT_DEFAULTS.id,
        })
      );
    }
  }

  if (dataType === restoredType && defaultData === restoredDefaultData) {
    return component;
  }

  return Component.update(component, {
    dataType: restoredType,
    defaultData: restoredDefaultData,
  });
};

const getPropertyBindingKey = matchExpression({
  Select: ({from, prop}) => {
    if (Expressions.isDataVariable(from)) {
      return prop;
    }
    return null;
  },
  _: () => null,
});

/**
 * Return the variant key for the current component based on the variantIdMap.
 * If the base variant is selected, return null otherwise return the current variant key.
 */
const getVariantKey = (
  component: Component.ComponentType,
  variantIdMap: {[key: string]: string}
): VariantStateKey | null => {
  const variants = component.variants;
  if (!variants) {
    return null;
  } else {
    const variantFieldId = Object.keys(variants)[0];
    if (!variantFieldId) {
      return null;
    }
    let currentKey = variantFieldId;
    while (variantIdMap[currentKey]) {
      const newKey = variantIdMap[currentKey];
      if (!newKey) {
        // If the base variant is selected, return null
        // Otherwise, return the current variant key
        return currentKey !== BASE_VARIANT_DEFAULTS.id
          ? stringifyVariantStateKey(variantFieldId, currentKey)
          : null;
      }
      currentKey = newKey;
    }
    // If the base variant is selected, return null
    // Otherwise, return the current variant key
    return currentKey !== BASE_VARIANT_DEFAULTS.id
      ? stringifyVariantStateKey(variantFieldId, currentKey)
      : null;
  }
};

/**
 * Gets current active variant by traversing the nativeIdPath and collecting all the style variants till the selectedNodeId.
 * Returns the topmost override or default VariantStateKey. If no variant is found or the selected variant is the base variant, returns null.
 */
export const getCurrentActiveVariant = (
  componentMap: Component.ComponentMap,
  initialComponent: Component.ComponentType,
  nativeIdPath: string[],
  selectedNodeId: string,
  plugins: Plugins.PluginsType
): VariantStateKey | null => {
  let currentComponent = initialComponent;
  const selectedElementId = getGuidNullable(selectedNodeId);
  const variantIdMap: {[key: string]: string} = {};
  for (const nativeId of nativeIdPath) {
    const elementId = getGuidNullable(nativeId);
    if (!elementId) {
      return null;
    }
    // If we have reached the selected element, we can stop traversing
    if (elementId === selectedElementId) {
      return getVariantKey(currentComponent, variantIdMap);
    }
    const element = Expressions.getElementById(
      currentComponent.render,
      elementId
    );
    // If we cannot find the element continue traversing the nativeIdPath
    if (!element) {
      continue;
    }
    const component = componentMap.get(element.type);
    if (!component) {
      return null;
    }
    const currentTypeFields = Types.getRecordFields(component.dataType);
    const data = element ? Expressions.getElementData(element) : null;
    const instDataFields = data ? Expressions.getRecord(data) : null;
    // If there are no dataType fields, skip to the next component
    if (!currentTypeFields) {
      currentComponent = component;
      continue;
    }
    // Collect all Style Variant bindings, overrides, and defaults
    for (const [fieldId, typeField] of Object.entries(currentTypeFields)) {
      // If the field is not a style variant type, skip it
      if (!Types.isStyleVariantType(typeField)) {
        continue;
      }
      const instDataField = instDataFields
        ? instDataFields[fieldId]
        : undefined;
      // Check if there is a binding
      const dataKey = instDataField
        ? getPropertyBindingKey(instDataField)
        : null;
      if (dataKey) {
        variantIdMap[fieldId] = dataKey;
      }
      // If there is no binding then there should be an override or default value
      else {
        const currentOrDefaultExpression = getCurrentOrDefaultExpression(
          plugins
        )(typeField, instDataField);
        const currentOrDefaultLiteral = Expressions.getLiteral(
          currentOrDefaultExpression
        );
        if (
          currentOrDefaultLiteral &&
          typeof currentOrDefaultLiteral.value === 'string'
        ) {
          variantIdMap[fieldId] = currentOrDefaultLiteral.value;
        }
      }
    }
    currentComponent = component;
  }
  return getVariantKey(currentComponent, variantIdMap);
};

export const isVariantTypeFromCurrentComponent = (
  type: Type,
  pluginName: string,
  componentName: string
) => {
  if (!Types.isStyleVariantType(type)) {
    return false;
  }
  const {
    variantFieldId: _,
    componentName: componentNameArgument,
    pluginName: pluginNameArgument,
  } = getStyleVariantTypeArguments(type);
  return (
    componentNameArgument === componentName && pluginNameArgument === pluginName
  );
};

/**
 * Given an instance element:
 * If a non-default variant is selected, return the current VariantStateKey
 * If a non-default base variant is selected, return the current VariantStateKey
 *
 * If the instance has the default base variant selected, return null
 */
export const getCurrentVariantStateKeyFromElement = (
  instanceElement: ExpressionElement,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): VariantStateKey | null => {
  if (!isUserComponentInst(componentMap, instanceElement)) return null;
  const component = componentMap.get(instanceElement.type);
  if (!component) return null;

  const variants = Component.getVariants(component);
  if (!variants) return null;

  // We currently support only one variant field
  const variantFieldId = Object.keys(variants)[0];
  if (!variantFieldId) return null;

  const instData = Expressions.getElementData(instanceElement);
  const fields = Expressions.getRecord(instData);
  const variantOptionIdExpr = fields?.[variantFieldId];

  if (!variantOptionIdExpr) {
    const defaultValue = Types.getTypeDefaultValue(plugins)(component.dataType);
    if (variantFieldId in defaultValue) {
      const variantOptionId = defaultValue[variantFieldId];
      if (variantOptionId === BASE_VARIANT_DEFAULTS.id) return null;
      return stringifyVariantStateKey(variantFieldId, variantOptionId);
    }
    return null;
  }

  const variantOptionId = Expressions.getLiteral(variantOptionIdExpr)?.value as
    | string
    | undefined;

  if (!variantOptionId || variantOptionId === BASE_VARIANT_DEFAULTS.id) {
    return null;
  }

  return stringifyVariantStateKey(variantFieldId, variantOptionId);
};

/**
 * Given a root component and a current variant state key,
 * return the display name of the current variant
 *
 * if the variant option id is not found, return null
 */
export const getVariantDisplayName = (
  rootComponent: Component.ComponentType,
  currentVariantStateKey: VariantStateKey
): string | null => {
  const [variantFieldId, variantOptionId] = parseVariantStateKey(
    currentVariantStateKey
  );
  const styleVariants = Component.getVariants(rootComponent)?.[variantFieldId];
  const styleVariant = styleVariants?.find(
    (variant) => variant.id === variantOptionId
  );
  return styleVariant?.displayName ?? null;
};

/**
 * Creates an `AttributeMap` for a given component and active variants that contains data attributes
 * to be applied to the component's root elements for each active variant.
 *
 * These data attributes are intended for use in custom CSS attribute selectors to enable
 * targeting active component style variants with user-provided custom CSS.
 *
 * These attributes use the following syntax:
 * ```
 * `data-wf--${LIBRARY_SLUG}--${COMPONENT_SLUG}--${VARIANT_FIELD_SLUG}="${VARIANT_OPTION_SLUG}"`
 * ```
 * Where each slug represents a CSS-safe selector string, the combination of which is used to namespace
 * the attribute and avoid conflicts across components / style variant properties:
 *
 *  - `LIBRARY_SLUG`         : the library's slug, if the component is shared in a library
 *  - `COMPONENT_SLUG`       : the component's display name
 *  - `VARIANT_FIELD_SLUG`   : the style variant field's display name
 *  - `VARIANT_OPTION_SLUG`  : a specific variant option's display name from within the style variant field
 *
 * Example of a site-level component named `Button Component` with a `Variant` field set to `Danger`:
 * ```
 * 'data-wf--button-component--variant="danger"'
 * ```
 *
 * Example of the same component shared via `My Library`:
 * ```
 * 'data-wf--my-library--button-component--variant="danger"'
 * ```
 */
export const createComponentVariantsAttributeMap = weakMemo(
  (slugMap: Plugins.PluginsType['slugMap']) =>
    weakMemo((component: Component.ComponentType) =>
      weakMemo((activeVariants: ActiveVariants): AttributesMap => {
        const variantAttrMap = IMap<string, string>();
        const {dataType, displayName, origin, variants} = component;
        const fields = Types.getRecordFields(dataType);

        if (!fields || !variants) return variantAttrMap;

        const componentNamePrefix = createClassName(
          displayName,
          origin ? getUserlandPrefix(origin, slugMap) : null
        );

        return variantAttrMap.withMutations((map) => {
          for (const variantStateKey of Object.keys(activeVariants)) {
            const [variantFieldId, variantOptionId] = parseVariantStateKey(
              variantStateKey as VariantStateKey
            );

            const variantFieldName = fields[variantFieldId]?.system?.label;
            if (variantFieldName == null) continue;

            const variantOptions = variants[variantFieldId];
            if (variantOptions == null) continue;

            const variantOption = variantOptions.find(
              ({id}) => id === variantOptionId
            );
            if (variantOption == null) continue;

            // As the data attribute is intended for use as a CSS selector in user-provided custom CSS,
            // we use `createClassName` to ensure parts of the attribute name and value are transformed
            // to values that are safe for use in CSS selector strings.
            const fieldCssSafeName = createClassName(
              variantFieldName,
              componentNamePrefix
            );
            const optionCssSafeName = createClassName(
              variantOption.displayName,
              null
            );

            map.merge({
              [`data-wf--${fieldCssSafeName}`]: optionCssSafeName,
            });
          }
        });
      })
    )
);

/**
 * If the supplied variantId is the default value of the style variant dataType field,
 * then update the default value to Base.
 */
export function replaceDeletedStyleVariantDefaultValue(
  component: Component.ComponentType,
  variantFieldId: string,
  deletedVariantId: string
): Component.ComponentType {
  const dataType = Component.getDataType(component);
  const dataTypeFields = Types.getRecordFields(dataType);
  const styleVariantField = dataTypeFields?.[variantFieldId];
  if (!styleVariantField || !Types.isStyleVariantType(styleVariantField)) {
    return component;
  }
  const system = getSystem(styleVariantField);
  if (!system) return component;
  if (system.defaultValue !== deletedVariantId) return component;

  const nextDataType = TRecord({
    ...dataTypeFields,
    [variantFieldId]: withSystem(
      {
        ...system,
        defaultValue: BASE_VARIANT_DEFAULTS.id,
      },
      styleVariantField
    ),
  });

  const defaultData = Component.getDefaultData(component);
  let nextDefaultData = defaultData;
  if (nextDefaultData) {
    const defaultDataPath: ExpressionPath = [
      {in: 'Function'},
      {in: 'Record', at: variantFieldId},
    ];
    const existing = Expressions.getIn(nextDefaultData, defaultDataPath);
    const existingLiteralValue = existing
      ? Expressions.getLiteral(existing)?.value
      : undefined;
    if (existingLiteralValue === deletedVariantId) {
      nextDefaultData = Expressions.sparseSetIn(
        nextDefaultData,
        defaultDataPath,
        ELiteral({
          name: qualify(Plugins.name('Basic'), typeName('StyleVariant')),
          value: BASE_VARIANT_DEFAULTS.id,
        })
      );
    }
  }

  if (nextDataType === dataType && nextDefaultData === defaultData) {
    return component;
  }

  return Component.update(component, {
    dataType: nextDataType,
    defaultData: nextDefaultData,
  });
}

/**
 * Removes the variant field identified by `fieldId` from the component's
 * variants object. Only removes if the field is a style variant type.
 * Returns the updated variants (without the field key), or the original
 * variants if the field doesn't exist or isn't a style variant.
 */
export function removeVariantByFieldId(
  component: Component.ComponentType,
  fieldId: string,
  rootDataTypeFields: Record<string, Type>
): Component.Variants | undefined {
  const fieldToDelete = rootDataTypeFields[fieldId];
  if (!fieldToDelete) {
    return component.variants;
  }
  if (Types.isStyleVariantType(fieldToDelete) && component.variants) {
    return dissoc(fieldId)(component.variants);
  }
  return component.variants;
}

/**
 * Extract style variants for a given variantFieldId on a ComponentType as an
 * object where the key is the variant id and the value is the variant
 * displayName.
 */
export function getStyleVariantMap(
  comp: Component.ComponentType,
  variantFieldId?: string
) {
  const result: {[key: string]: string} = {};
  const variants = comp.variants;

  if (variants) {
    const styleVariants = variantFieldId
      ? variants[variantFieldId]
      : Object.values(variants)[0];

    if (styleVariants) {
      styleVariants.forEach((variant: Component.StyleVariant) => {
        result[variant.id] = variant.displayName;
      });
    }
  }
  return result;
}

export const confirmVariantDeletion = async (
  variantName: string,
  deletedInteractionCount = 0,
  modifiedInteractionCount = 0
) => {
  const {t} = designerTranslation;

  const baseDescription = t(
    'designer.components.style-variants.delete-variant.description',
    {variantName}
  );

  let warning: string | undefined;
  if (deletedInteractionCount > 0) {
    warning = t(
      'designer.components.style-variants.delete-variant.interactions-deleted',
      {
        count: deletedInteractionCount,
        interactionPluralized: pluralize(
          deletedInteractionCount,
          'interaction'
        ),
        variantName,
      }
    );
  } else if (modifiedInteractionCount > 0) {
    warning = t(
      'designer.components.style-variants.delete-variant.interactions-modified',
      {
        count: modifiedInteractionCount,
        interactionPluralized: pluralize(
          modifiedInteractionCount,
          'interaction'
        ),
        variantName,
      }
    );
  }

  const content = warning
    ? React.createElement(
        React.Fragment,
        null,
        React.createElement('div', null, baseDescription),
        React.createElement(View, {marginTop: '$2'}, warning)
      )
    : baseDescription;

  const response = await confirm({
    title: t('designer.components.style-variants.delete-variant.title'),
    content,
    submit: {
      label: t('designer.components.style-variants.delete-variant.delete-cta'),
      intent: 'danger',
      'data-automation-id': 'delete-variant-confirm-button',
    },
    cancel: {
      label: t('designer.components.style-variants.delete-variant.cancel-cta'),
      intent: 'default',
      'data-automation-id': 'delete-variant-cancel-button',
    },
    style: {maxWidth: '300px'},
    'data-automation-id': 'delete-variant-modal',
  });

  return response.type === 'confirm';
};

export {variantStateSelected} from './actions';
export * from './constants';
export * from './utils/baseVariantUtils';
